import React, { useMemo } from 'react';
import { calculateFinancials } from '../../services/financialCalculations';
import { formatCurrency, formatPercentage } from '../../utils/formatters';

const BessEfficiencyReplacementSensitivityTable = ({ inputs }) => {
    const { sensitivityData, efficiencyRange, replacementFactorRange } = useMemo(() => {
        const baseEfficiency = inputs.bessRoundTripEfficiency;
        const baseReplacementFactor = inputs.bessReplacementCostFactor;

        const efficiencyRange = [-0.1, -0.05, 0, 0.05, 0.1].map(mod => Math.max(0.7, Math.min(0.95, baseEfficiency * (1 + mod))));
        const replacementFactorRange = [-0.3, -0.15, 0, 0.15, 0.3].map(mod => baseReplacementFactor * (1 + mod));

        const sensitivityData = replacementFactorRange.map(factor => {
            return efficiencyRange.map(efficiency => {
                const tempInputs = { ...inputs, bessRoundTripEfficiency: efficiency, bessReplacementCostFactor: factor };
                const results = calculateFinancials(tempInputs);
                return results.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction - b.operatingCost - b.replacementCapex), 0) || 0;
            });
        });

        return { sensitivityData, efficiencyRange, replacementFactorRange };
    }, [inputs]);

    if (!inputs.bessEnabled) return null;

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-purple-800 dark:text-purple-100 mb-2">🔋 BESS Efficiency vs Replacement Cost Factor (Total BESS Cash Flow)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-purple-300 dark:border-purple-600">
                    <thead className="bg-purple-100 dark:bg-purple-700">
                        <tr>
                            <th className="p-2 border border-purple-300 dark:border-purple-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Replacement Factor ↓</span>
                                    <span className="font-normal text-xs">Efficiency →</span>
                                </div>
                            </th>
                            {efficiencyRange.map((efficiency, index) => (
                                <th key={index} className="p-2 border border-purple-300 dark:border-purple-600 font-medium">{formatPercentage(efficiency)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sensitivityData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="p-2 border border-purple-300 dark:border-purple-600 font-medium bg-purple-50 dark:bg-purple-700">
                                    {formatPercentage(replacementFactorRange[rowIndex])}
                                </td>
                                {row.map((cashFlow, colIndex) => (
                                    <td key={colIndex} className={`p-2 border border-purple-300 dark:border-purple-600 ${cashFlow > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}>
                                        {formatCurrency(cashFlow, 0)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BessEfficiencyReplacementSensitivityTable; 